/**
 * Cross-page seed for the Home agent: Assets/Templates pick an avatar, voice
 * or style → Home reads it once and pre-selects the chips.
 */
const KEY = 'vivideo_v2_seed';

export interface HomeSeed {
  avatarId?: string;
  avatarName?: string;
  avatarImg?: string;
  voiceId?: string;
  voiceName?: string;
  styleId?: string;
  styleName?: string;
  styleImg?: string;
  stylePreviewVideo?: string;
  brandRef?: string;
  brandName?: string;
  brandLogo?: string;
  // A library file to pre-attach to the next agent session ({asset_id} shape).
  fileAssetId?: string;
  fileName?: string;
  filePreviewUrl?: string;
  fileType?: string;
}

export function seedHomeAgent(seed: HomeSeed) {
  try {
    const prev = JSON.parse(sessionStorage.getItem(KEY) || '{}');
    sessionStorage.setItem(KEY, JSON.stringify({ ...prev, ...seed }));
  } catch { /* best effort */ }
}

export function consumeHomeSeed(): HomeSeed | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    sessionStorage.removeItem(KEY);
    return JSON.parse(raw) as HomeSeed;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Home MANUAL-mode seed — Templates v2 "Use this" pre-fills the manual composer
// (model + prompt + duration + aspect/resolution). One-shot: Home consumes it on
// mount, switches to Manual mode and applies the values (snapping any invalid
// ones to the model's defaults). Distinct from the AGENT seed above.
// ---------------------------------------------------------------------------
const MANUAL_KEY = 'vivideo_v2_manual_seed';

export interface HomeManualSeed {
  modelId?: string;
  operation?: string; // OperationType — kept as string to avoid a models import here
  prompt?: string;
  duration?: string;
  aspectRatio?: string;
  resolution?: string;
  negativePrompt?: string;
  // Provenance (analytics / a "from template" strip if we want one later).
  templateId?: string;
  templateName?: string;
}

export function seedHomeManual(seed: HomeManualSeed) {
  try {
    sessionStorage.setItem(MANUAL_KEY, JSON.stringify(seed));
  } catch { /* best effort */ }
}

/** Read WITHOUT clearing (so Home can decide to switch to Manual mode first). */
export function peekHomeManualSeed(): HomeManualSeed | null {
  try {
    const raw = sessionStorage.getItem(MANUAL_KEY);
    return raw ? (JSON.parse(raw) as HomeManualSeed) : null;
  } catch {
    return null;
  }
}

/** Read AND clear (Manual mode applies it exactly once). */
export function consumeHomeManualSeed(): HomeManualSeed | null {
  try {
    const raw = sessionStorage.getItem(MANUAL_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(MANUAL_KEY);
    return JSON.parse(raw) as HomeManualSeed;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Order prompt memory — the composer knows the exact prompt the user submitted
// for a MANUAL/auto order, but the project-load API doesn't echo it back. We
// stash prompt-by-orderId locally so the project page can show "Prompt: …"
// above the video. Capped LRU-ish map (keep the most recent ~120 orders).
// ---------------------------------------------------------------------------
const ORDER_PROMPT_KEY = 'vivideo_v2_order_prompts';
const ORDER_PROMPT_MAX = 120;

type OrderPromptMap = Record<string, string>;

function _readOrderPrompts(): OrderPromptMap {
  try {
    const raw = window.localStorage.getItem(ORDER_PROMPT_KEY);
    const m = raw ? JSON.parse(raw) : {};
    return m && typeof m === 'object' ? (m as OrderPromptMap) : {};
  } catch { return {}; }
}

export function rememberOrderPrompt(orderId: string | undefined | null, prompt: string | undefined | null): void {
  const id = (orderId || '').trim();
  const p = (prompt || '').trim();
  if (!id || !p) return;
  try {
    const m = _readOrderPrompts();
    // Re-insert at the end so recent orders survive the cap (JS objects keep
    // insertion order for string keys).
    delete m[id];
    m[id] = p;
    const keys = Object.keys(m);
    if (keys.length > ORDER_PROMPT_MAX) {
      for (const k of keys.slice(0, keys.length - ORDER_PROMPT_MAX)) delete m[k];
    }
    window.localStorage.setItem(ORDER_PROMPT_KEY, JSON.stringify(m));
  } catch { /* best effort */ }
}

export function readOrderPrompt(orderId: string | undefined | null): string | null {
  const id = (orderId || '').trim();
  if (!id) return null;
  const m = _readOrderPrompts();
  return m[id] || null;
}

// ---------------------------------------------------------------------------
// Applied template (a HeyGen style) — persisted per browser so the choice made
// on the Templates page survives navigation until the user detaches it.
// ---------------------------------------------------------------------------
export type AppliedTemplate = { id: string; name: string; img?: string; video?: string };
const TEMPLATE_KEY = 'vivideo_v2_applied_template';

export function readAppliedTemplate(): AppliedTemplate | null {
  try {
    const raw = window.localStorage.getItem(TEMPLATE_KEY);
    return raw ? (JSON.parse(raw) as AppliedTemplate) : null;
  } catch { return null; }
}

export function writeAppliedTemplate(tpl: AppliedTemplate | null): void {
  try {
    if (tpl) window.localStorage.setItem(TEMPLATE_KEY, JSON.stringify(tpl));
    else window.localStorage.removeItem(TEMPLATE_KEY);
  } catch { /* best effort */ }
}

// ---------------------------------------------------------------------------
// Applied cast (avatar / voice / brand) — persisted per browser just like the
// template above, so a pick made on the Assets page or a selector SURVIVES
// navigation until the user clears it. (Previously these rode the one-shot seed
// and vanished on the next Home remount.)
// ---------------------------------------------------------------------------
export interface AppliedPick {
  kind: string;
  id: string;
  label: string;
  imageUrl?: string;
  previewAudioUrl?: string;
  gender?: string;
}
export interface AppliedCast {
  avatar: AppliedPick | null;
  voice: AppliedPick | null;
  brand: AppliedPick | null;
}
const CAST_KEY = 'vivideo_v2_applied_cast';

export function readAppliedCast(): AppliedCast {
  try {
    const raw = window.localStorage.getItem(CAST_KEY);
    const c = raw ? JSON.parse(raw) : {};
    return { avatar: c.avatar || null, voice: c.voice || null, brand: c.brand || null };
  } catch {
    return { avatar: null, voice: null, brand: null };
  }
}

export function writeAppliedCast(cast: AppliedCast): void {
  try {
    if (!cast.avatar && !cast.voice && !cast.brand) window.localStorage.removeItem(CAST_KEY);
    else window.localStorage.setItem(CAST_KEY, JSON.stringify(cast));
  } catch { /* best effort */ }
}
